import React, { useState } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDatabase } from '../hooks/useDatabase';
import CodeManager from '../components/domain/CodeManager';
import { generateSecureCode } from '../utils/codeGenerator';
import { SIZE_LABELS, getSizeCategoryLabel } from '../utils/trasterosSize';

const IN_USE_STATES = ['activo', 'libre', 'pendiente', 'bloqueado'];

const EMPTY = {
  numero_reserva: '',
  cliente_id: '',
  trastero_id: '',
  fecha_solicitud: '',
  fecha_entrada_prevista: '',
  estado: 'pendiente',
  canal: 'web',
  observaciones: ''
};

const Reservas = () => {
  const { data, loading, error, refresh, insert, update, remove } = useDatabase('reservas');
  const { data: clientes } = useDatabase('clientes');
  const { data: trasteros } = useDatabase('trasteros');
  const { data: codigos, update: updateCodigo, insert: insertCodigo } = useDatabase('codigos');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [contractData, setContractData] = useState(null);

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      numero_reserva: item.numero_reserva,
      cliente_id: item.cliente_id || '',
      trastero_id: item.trastero_id || '',
      fecha_solicitud: item.fecha_solicitud || '',
      fecha_entrada_prevista: item.fecha_entrada_prevista || '',
      estado: item.estado,
      canal: item.canal,
      observaciones: item.observaciones || ''
    });
    setEditingId(item.id);
    setIsModalOpen(true);
  };

  const openContractView = (item) => {
    const client = clientes.find(c => c.id === item.cliente_id) || {};
    const trastero = trasteros.find(t => t.id === item.trastero_id) || {};
    setContractData({ reserva: item, cliente: client, trastero: trastero });
    setIsContractModalOpen(true);
  };

  const handleCopyContractData = () => {
    if (!contractData) return;
    const { reserva, cliente, trastero } = contractData;
    
    const catLabel = trastero.categoria 
      ? (SIZE_LABELS[trastero.categoria] || trastero.categoria) 
      : (trastero.size ? getSizeCategoryLabel(trastero.size) : '---');

    const textToCopy = `DATOS PARA CONTRATO DE ALQUILER
================================
Nº Reserva: ${reserva.numero_reserva}
Fecha Solicitud: ${reserva.fecha_solicitud || '---'}
Fecha Entrada Prevista: ${reserva.fecha_entrada_prevista || '---'}
Observaciones: ${reserva.observaciones || '---'}

DATOS DEL ARRENDATARIO (CLIENTE)
--------------------------------
Nombre / Empresa: ${cliente.nombre_completo || '---'}
DNI / NIE / CIF: ${cliente.documento || '---'}
Dirección: ${cliente.direccion || '---'}
Teléfono: ${cliente.telefono || '---'}
Email: ${cliente.email || '---'}

DATOS DEL ESPACIO (TRASTERO)
--------------------------------
Identificador: ${trastero.codigo || '---'}
Categoría: ${catLabel}
Ubicación: ${trastero.zona || '---'}
Superficie: ${trastero.size ? `${trastero.size} m²` : '---'}
Precio Mensual: ${trastero.precio_mensual ? `${trastero.precio_mensual} €` : '---'}
`;

    navigator.clipboard.writeText(textToCopy)
      .then(() => alert('Datos copiados al portapapeles. Listos para pegar.'))
      .catch(err => alert('Error al copiar: ' + err));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        cliente_id: form.cliente_id || null,
        trastero_id: form.trastero_id || null
      };

      let resultId = editingId;
      if (editingId) {
        await update(editingId, payload);
      } else {
        const result = await insert(payload);
        resultId = result.id;
      }

      // --- ASIGNACIÓN AUTOMÁTICA DE CÓDIGO ---
      if (payload.cliente_id && payload.trastero_id) {
        const clientActiveCode = codigos.find(c => c.cliente_id === payload.cliente_id && (c.estado === 'activo' || c.estado === 'pendiente'));
        
        if (!clientActiveCode) {
          const libreCode = codigos.find(c => c.trastero_id === payload.trastero_id && c.estado === 'libre');

          if (libreCode) {
            await updateCodigo(libreCode.id, {
              cliente_id: payload.cliente_id,
              reserva_id: resultId,
              estado: 'activo',
              fecha_activacion: new Date().toISOString(),
              sincronizacion_puerta: 'pendiente_alta'
            });
          } else {
            const activeCodesList = codigos
              .filter(c => IN_USE_STATES.includes(c.estado))
              .map(c => c.codigo);
            const newCode = generateSecureCode(activeCodesList);

            await insertCodigo({
              codigo: newCode,
              cliente_id: payload.cliente_id,
              trastero_id: payload.trastero_id,
              reserva_id: resultId,
              estado: 'activo',
              fecha_activacion: new Date().toISOString(),
              sincronizacion_puerta: 'pendiente_alta',
              notas_internas: 'Generado automáticamente al crear reserva'
            });
          }
        }
      }
      // ---------------------------------------

      setIsModalOpen(false);
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta reserva?')) return;
    try {
      await remove(id);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const clienteNombre = (id) => clientes.find(c => c.id === id)?.nombre_completo || '—';
  const trasteroCodigo = (id) => trasteros.find(t => t.id === id)?.codigo || '—';

  const sortedTrasteros = [...trasteros].sort((a, b) => (a.codigo || '').localeCompare(b.codigo || '', 'es', { numeric: true }));
  const sortedClientes = [...clientes].sort((a, b) => (a.nombre_completo || '').localeCompare(b.nombre_completo || '', 'es'));

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark flex items-center gap-2">
            Reservas
            {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-lg" />}
          </h2>
          <p className="text-sm sm:text-base text-brand-text">Control de solicitudes y próximas entradas.</p>
        </div>
        <Button onClick={openNew} className="w-full sm:w-auto flex justify-center items-center gap-2">
          <SafeIcon icon={FiIcons.FiPlus} />
          Nueva Reserva
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3 text-sm">
          <SafeIcon icon={FiIcons.FiAlertTriangle} /><span>Error: {error}</span>
        </div>
      )}

      <Card>
        <div className="p-3 sm:p-4 border-b border-gray-100 flex justify-end">
          <Button variant="ghost" onClick={refresh} className="w-full sm:w-auto flex justify-center">
            <SafeIcon icon={FiIcons.FiRefreshCw} className={loading ? 'animate-spin' : ''} />
            <span className="ml-2 sm:hidden">Actualizar</span>
          </Button>
        </div>
        
        <div className="overflow-x-auto hide-scrollbar">
          {!loading && data.length === 0 ? (
            <p className="text-center text-brand-text py-12 text-sm">No hay reservas registradas.</p>
          ) : (
            <table className="w-full text-left text-sm min-w-[600px]">
              <thead className="bg-gray-50 text-brand-text">
                <tr>
                  <th className="px-6 py-3 font-medium">Nº Reserva</th>
                  <th className="px-6 py-3 font-medium">Cliente</th>
                  <th className="px-6 py-3 font-medium">Trastero</th>
                  <th className="px-6 py-3 font-medium">Fecha Entrada</th>
                  <th className="px-6 py-3 font-medium">Canal</th>
                  <th className="px-6 py-3 font-medium">Estado</th>
                  <th className="px-6 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-brand-dark">{item.numero_reserva}</td>
                    <td className="px-6 py-4 text-brand-text">{clienteNombre(item.cliente_id)}</td>
                    <td className="px-6 py-4 text-brand-text">{trasteroCodigo(item.trastero_id)}</td>
                    <td className="px-6 py-4 text-brand-text">{item.fecha_entrada_prevista || '—'}</td>
                    <td className="px-6 py-4 text-brand-text capitalize">{item.canal}</td>
                    <td className="px-6 py-4"><Badge variant={item.estado}>{item.estado}</Badge></td>
                    <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                      <button 
                        className="text-gray-400 hover:text-green-600 p-2 sm:p-1 transition-colors"
                        onClick={() => openContractView(item)}
                        title="Ver datos para contrato"
                      >
                        <SafeIcon icon={FiIcons.FiFileText} />
                      </button>
                      <button 
                        className="text-gray-400 hover:text-brand-primary p-2 sm:p-1 transition-colors"
                        onClick={() => openEdit(item)}
                        title="Editar reserva"
                      >
                        <SafeIcon icon={FiIcons.FiEdit2} />
                      </button>
                      <button 
                        className="text-gray-400 hover:text-brand-alert p-2 sm:p-1 transition-colors"
                        onClick={() => handleDelete(item.id)}
                        title="Eliminar reserva"
                      >
                        <SafeIcon icon={FiIcons.FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* MODAL DE EDICIÓN / NUEVA RESERVA */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }}
        title={editingId ? 'Ficha de Reserva' : 'Nueva Reserva'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <Input 
            label="Nº de Reserva" 
            placeholder="R-1003" 
            value={form.numero_reserva} 
            onChange={e => setForm({...form, numero_reserva: e.target.value})} 
            required
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Cliente</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary"
                value={form.cliente_id}
                onChange={e => setForm({...form, cliente_id: e.target.value})}
              >
                <option value="">— Sin asignar —</option>
                {sortedClientes.map(c => <option key={c.id} value={c.id}>{c.nombre_completo}</option>)}
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Trastero</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary"
                value={form.trastero_id}
                onChange={e => setForm({...form, trastero_id: e.target.value})}
              >
                <option value="">— Sin asignar —</option>
                {sortedTrasteros.map(t => <option key={t.id} value={t.id}>{t.codigo} – {t.zona}</option>)}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input 
              label="Fecha solicitud" 
              type="date" 
              value={form.fecha_solicitud} 
              onChange={e => setForm({...form, fecha_solicitud: e.target.value})} 
            />
            <Input 
              label="Fecha entrada prevista" 
              type="date" 
              value={form.fecha_entrada_prevista} 
              onChange={e => setForm({...form, fecha_entrada_prevista: e.target.value})} 
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Estado</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary"
                value={form.estado}
                onChange={e => setForm({...form, estado: e.target.value})}
              >
                <option value="pendiente">Pendiente</option>
                <option value="confirmada">Confirmada</option>
                <option value="ocupada">Ocupada</option>
                <option value="finalizada">Finalizada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Canal</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary"
                value={form.canal}
                onChange={e => setForm({...form, canal: e.target.value})}
              >
                <option value="web">Web</option>
                <option value="telefono">Teléfono</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="presencial">Presencial</option>
                <option value="referido">Referido</option>
              </select>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text mb-1">Observaciones</label>
            <textarea 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary text-sm"
              rows={2}
              value={form.observaciones}
              onChange={e => setForm({...form, observaciones: e.target.value})}
            />
          </div>

          {editingId && form.cliente_id && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium text-brand-dark">Acceso al Centro</h4>
                {(form.estado === 'finalizada' || form.estado === 'cancelada') && (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <SafeIcon icon={FiIcons.FiAlertCircle} /> Al guardar, rotará.
                  </span>
                )}
              </div>
              <CodeManager clienteId={form.cliente_id} trasteroId={form.trastero_id} />
            </div>
          )}
          
          <div className="pt-4 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-gray-100 mt-4">
            <Button 
              variant="ghost" 
              onClick={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }} 
              type="button"
              className="w-full sm:w-auto"
            >
              Cerrar
            </Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar Reserva'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* MODAL DE DATOS DE CONTRATO */}
      <Modal isOpen={isContractModalOpen} onClose={() => setIsContractModalOpen(false)} title="Datos para Contrato">
        {contractData ? (
          <div className="space-y-6">
            <div className="bg-purple-50/50 p-4 rounded-lg border border-purple-100">
              <h4 className="text-xs font-bold uppercase tracking-wider text-purple-600 mb-3 border-b border-purple-100 pb-2">Información de la Reserva</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 text-sm">
                <div>
                  <span className="text-purple-500/80 block text-xs">Nº Reserva</span>
                  <strong className="text-brand-dark">{contractData.reserva.numero_reserva || '—'}</strong>
                </div>
                <div>
                  <span className="text-purple-500/80 block text-xs">Fechas</span>
                  <span className="text-brand-dark block">Sol: {contractData.reserva.fecha_solicitud || '—'}</span>
                  <span className="text-brand-dark block">Ent: {contractData.reserva.fecha_entrada_prevista || '—'}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-purple-500/80 block text-xs">Observaciones</span>
                  <span className="text-brand-dark whitespace-pre-wrap">{contractData.reserva.observaciones || '—'}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 border-b border-gray-200 pb-2">Información del Cliente</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                <div><span className="text-gray-500 block text-xs">Nombre/Empresa</span><strong className="text-brand-dark">{contractData.cliente.nombre_completo || '—'}</strong></div>
                <div><span className="text-gray-500 block text-xs">DNI/NIE/CIF</span><strong className="text-brand-dark">{contractData.cliente.documento || '—'}</strong></div>
                <div className="sm:col-span-2"><span className="text-gray-500 block text-xs">Dirección</span><span className="text-brand-dark">{contractData.cliente.direccion || '—'}</span></div>
                <div><span className="text-gray-500 block text-xs">Teléfono</span><span className="text-brand-dark">{contractData.cliente.telefono || '—'}</span></div>
                <div><span className="text-gray-500 block text-xs">Email</span><span className="text-brand-dark">{contractData.cliente.email || '—'}</span></div>
              </div>
            </div>

            <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
              <h4 className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 border-b border-blue-100 pb-2">Trastero Asignado</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-sm">
                <div><span className="text-gray-500 block text-xs">Código</span><strong className="text-brand-dark">{contractData.trastero.codigo || '—'}</strong></div>
                <div>
                  <span className="text-gray-500 block text-xs">Categoría</span>
                  <span className="text-brand-dark">
                    {contractData.trastero.categoria 
                      ? (SIZE_LABELS[contractData.trastero.categoria] || contractData.trastero.categoria) 
                      : (contractData.trastero.size ? getSizeCategoryLabel(contractData.trastero.size) : '—')}
                  </span>
                </div>
                <div><span className="text-gray-500 block text-xs">Zona</span><span className="text-brand-dark">{contractData.trastero.zona || '—'}</span></div>
                <div><span className="text-gray-500 block text-xs">Superficie</span><span className="text-brand-dark">{contractData.trastero.size ? `${contractData.trastero.size} m²` : '—'}</span></div>
                <div className="sm:col-span-2"><span className="text-gray-500 block text-xs">Precio Mensual</span><strong className="text-brand-dark">{contractData.trastero.precio_mensual ? `${contractData.trastero.precio_mensual} €` : '—'}</strong></div>
              </div>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={() => setIsContractModalOpen(false)} className="w-full sm:w-auto">
                Cerrar
              </Button>
              <Button 
                onClick={handleCopyContractData} 
                className="w-full sm:flex-1 flex justify-center items-center gap-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
              >
                <SafeIcon icon={FiIcons.FiCopy} /> Copiar Datos para Contrato
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-8 flex justify-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-2xl" />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Reservas;